import { Navigate } from 'react-router-dom';

import { useUsageSelection } from '../hooks/useUsageSelection';
import { buildPlanStepPath } from '../services/funnel-rehydrate.service';
import { funnelGateFor } from '../services/funnelGating';
import { FUNNEL_STEP_ORDER, type FunnelStepId } from '../services/funnelSteps';
import { useFunnel } from '../state/FunnelProvider';

/**
 * Refuse to RENDER a funnel step the server cannot evidence yet (E-SF19 P1).
 *
 * ── WHAT IT STOPS ──────────────────────────────────────────────────────────
 * Direct-URL to `/plan/:slug/sign` on a session at step `plan` used to render the
 * whole sign surface — the documents, the acknowledgements, the pad, and a green
 * alert reading "No deposit required — you have no deposit to pay", asserted to a
 * customer whose credit had never been run.
 *
 * ── IT IS A DISPLAY GATE. IT IS NOT A CONTROL. ─────────────────────────────
 * The server refuses regardless (`POST /bff/sign/accept` → 400), and it must keep
 * doing so: a client-side gate protects nobody. What this fixes is that the page
 * was TELLING the customer things that were not true, and a button that would
 * eventually fail does not un-tell them.
 *
 * ── WHY A WRAPPER AND NOT A HOOK IN EACH PAGE ──────────────────────────────
 * The redirect has to happen BEFORE the step's own hooks run, or a gated page
 * still fires its queries — `/bff/estimate` is captcha-gated and hits GMO and
 * Pricing, so a bounced visit would spend the session's free allowance on a step
 * it is not allowed to see. Wrapping means the gated tree never mounts.
 *
 * The DECISION lives in `services/funnelGating.ts`; this component only renders
 * the outcome (CLAUDE.md — no business logic in `.tsx`).
 */
export interface FunnelStepGateProps {
  /** The step being requested. */
  readonly step: FunnelStepId;
  /** The plan slug, so a redirect lands on the same plan's earlier step. */
  readonly slug: string | undefined;
  readonly children: React.ReactNode;
}

export function FunnelStepGate({ step, slug, children }: FunnelStepGateProps): JSX.Element {
  const { serverStep } = useFunnel();
  const selection = useUsageSelection();

  const redirect = funnelGateFor({
    requestedStep: step,
    serverStep,
    order: FUNNEL_STEP_ORDER,
  });

  if (redirect === null) return <>{children}</>;

  // `null` = the funnel has not started (no resolved meter), so the chooser is the
  // destination. Same for a missing slug: there is no plan-scoped page to fall
  // back to.
  if (redirect.allowedStep === null || slug === undefined) {
    return <Navigate to="/" replace />;
  }
  // `plan` is the plan-DETAIL route, not a `/plan/:slug/<step>` sub-route.
  if (redirect.allowedStep === 'plan') {
    return <Navigate to={`/plan/${encodeURIComponent(slug)}`} replace />;
  }
  return (
    <Navigate
      to={buildPlanStepPath({ tariffId: slug, step: redirect.allowedStep, selection })}
      replace
    />
  );
}
